import { Button, Flex, Grid, Heading, Paragraph, PropertyCardPhoto, TextButton, token } from '@zillow/constellation';
import { useState } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import type { RentalApplication } from '../../../graphql/queries';
import { WithdrawApplication } from '../../WithdrawApplication';
import { getIsComplete, getIsWithdrawn } from '../../../utils/business-logic';
import { emitWithdrawModalEvent } from '../../../analytics/clickstreamEvents';
import { getCreateRoute } from '../../../constants/urls';
import { useApplicationContextOrUndefined } from '../../../contexts/ApplicationContext';

interface Props {
  application: RentalApplication;
}

const StyledPropertyCardPhoto = styled(PropertyCardPhoto)`
  min-height: 150px;
  border-radius: ${token('radii.md')}px;
`;

const ListingInfo = styled.span`
  &:not(:first-child):before {
    content: ' | ';
  }
`;

export function ApplicationOverviewListing({ application }: Props) {
  const router = useRouter();
  const applicationContext = useApplicationContextOrUndefined();
  const { rentalListing, applicationShareInformation } = application || {};
  const { address: listingAddress, listingAlias } = rentalListing || {};
  const { propertyAddress: sharedAddress, landlordEmail } = applicationShareInformation || {};
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const isComplete = getIsComplete(application);
  const isWithdrawn = getIsWithdrawn(application);
  const street = listingAddress?.street || sharedAddress?.street;
  const city = listingAddress?.city || sharedAddress?.city;
  const state = listingAddress?.state || sharedAddress?.state;
  const zip = listingAddress?.zip || sharedAddress?.zip;
  const unit = listingAddress?.unit || sharedAddress?.unit;

  const hasPhoto = !!rentalListing?.primaryPhoto?.imageUrl;

  return (
    <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="sm" marginBottom="sm">
      {hasPhoto && (
        <Grid gridColumnEnd={{ default: 'span 4', md_lte: 'span 12' }}>
          <StyledPropertyCardPhoto
            src={rentalListing?.primaryPhoto?.imageUrl as string}
            alt={`Listing photo for ${street}`}
          />
        </Grid>
      )}
      <Grid gridColumnEnd={{ default: `span ${hasPhoto ? '8' : '12'}`, md_lte: 'span 12' }}>
        <Flex
          display="flex"
          justifyContent="space-between"
          flexDirection={{ default: 'row', md_lte: 'column' }}
          gap="sm"
        >
          <Flex>
            <Heading level="3" as="h2">
              {street}
              {unit ? ` ${unit}` : ''}
            </Heading>
            <Paragraph marginTop="xs">
              {city}, {state} {zip}
            </Paragraph>
            {rentalListing && (
              <>
                <Paragraph marginTop="xs">
                  {rentalListing.monthlyPayment && (
                    <ListingInfo>
                      <strong>{rentalListing.monthlyPayment}</strong>/mo
                    </ListingInfo>
                  )}
                  {rentalListing.bedrooms != null && (
                    <ListingInfo>
                      <strong>{rentalListing.bedrooms}</strong> bd
                    </ListingInfo>
                  )}
                  {rentalListing.bathrooms != null && (
                    <ListingInfo>
                      <strong>{rentalListing.bathrooms as number}</strong> ba
                    </ListingInfo>
                  )}
                  {rentalListing.squareFeet && (
                    <ListingInfo>
                      <strong>{rentalListing.squareFeet.toLocaleString()}</strong> sq. ft.
                    </ListingInfo>
                  )}
                </Paragraph>
                {rentalListing.propertyUrl && (
                  <Paragraph marginTop="xs">
                    <TextButton as="a" href={rentalListing.propertyUrl as string}>
                      View listing
                    </TextButton>
                  </Paragraph>
                )}
              </>
            )}
            {landlordEmail && (
              <Paragraph marginTop="xs" fontType="bodySmall">
                {landlordEmail}
              </Paragraph>
            )}
          </Flex>
          {isComplete && (
            <Flex>
              <Button
                onClick={() => {
                  emitWithdrawModalEvent(applicationContext);
                  setIsWithdrawModalOpen(true);
                }}
                type="button"
              >
                Withdraw application
              </Button>
            </Flex>
          )}
          {isWithdrawn && listingAlias && (
            <Flex>
              <Button type="button" onClick={() => router.push(getCreateRoute(listingAlias))}>
                Reapply to this listing
              </Button>
            </Flex>
          )}
        </Flex>
      </Grid>
      <WithdrawApplication
        application={application}
        isOpen={isWithdrawModalOpen}
        onCloseModal={() => setIsWithdrawModalOpen(false)}
      />
    </Grid>
  );
}
